import type { ReactElement } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import type { Category, CommunityStructure } from "@atrium/shared";
import { api, errorMessage } from "../../lib/api";
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuSeparator, ContextMenuTrigger } from "../../components/ui/ContextMenu";
import { confirm } from "../../components/ui/Dialog";
import { toast } from "../../components/ui/Toast";
import { IconCheckRead, IconChevronDown, IconChevronRight, IconFolder, IconIdCard, IconLock, IconPlus, IconSettings, IconTrash } from "../../components/icons";
import { useMe } from "../auth/AuthProvider";
import { copyText } from "../posts/usePostActions";

/** Right-click (long-press) menu for a category header in the sidebar. `children` is the header row. */
export function CategoryContextMenu({
  structure,
  category,
  collapsed,
  onToggle,
  onCollapseAll,
  canManageChannels,
  canManagePermissions,
  onCreateChannel,
  onCreateCategory,
  children,
}: {
  structure: CommunityStructure;
  category: Category;
  collapsed: boolean;
  onToggle: () => void;
  onCollapseAll: () => void;
  canManageChannels: boolean;
  canManagePermissions: boolean;
  onCreateChannel: () => void;
  onCreateCategory: () => void;
  children: ReactElement;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const me = useMe();
  const communityId = structure.community.id;
  const key = ["structure", communityId];
  const unread = structure.channels.filter((c) => c.categoryId === category.id && (c.unread || c.mentionCount > 0));
  const settings = `/c/${communityId}/category/${category.id}/settings`;

  const markRead = async () => {
    try {
      await Promise.all(unread.map((c) => api.post(`/api/channels/${c.id}/read`, {})));
      const ids = new Set(unread.map((c) => c.id));
      qc.setQueryData<CommunityStructure>(key, (old) => old && { ...old, channels: old.channels.map((c) => (ids.has(c.id) ? { ...c, unread: false, mentionCount: 0 } : c)) });
      void qc.invalidateQueries({ queryKey: ["my-communities"] });
      void qc.invalidateQueries({ queryKey: ["unread"] });
    } catch (e) {
      toast.error(errorMessage(e, "Couldn't mark as read"));
    }
  };

  const remove = async () => {
    const ok = await confirm({ title: `Delete ${category.name}?`, description: "Channels in it move to the top of the list and keep their permissions.", confirmLabel: "Delete category", danger: true });
    if (!ok) return;
    try {
      await api.del(`/api/categories/${category.id}`);
      await qc.invalidateQueries({ queryKey: key });
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const copyId = async () => {
    await copyText(category.id);
    toast.show("Category ID copied");
  };

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem icon={IconCheckRead} disabled={!unread.length} onSelect={() => void markRead()}>
          Mark as read
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem icon={collapsed ? IconChevronDown : IconChevronRight} onSelect={onToggle}>
          {collapsed ? "Expand category" : "Collapse category"}
        </ContextMenuItem>
        <ContextMenuItem icon={IconChevronRight} onSelect={onCollapseAll}>
          Collapse all categories
        </ContextMenuItem>
        {(canManageChannels || canManagePermissions) && <ContextMenuSeparator />}
        {canManageChannels && (
          <ContextMenuItem icon={IconSettings} onSelect={() => navigate(`${settings}/overview`)}>
            Edit category
          </ContextMenuItem>
        )}
        {canManagePermissions && (
          <ContextMenuItem icon={IconLock} onSelect={() => navigate(`${settings}/permissions`)}>
            Category permissions
          </ContextMenuItem>
        )}
        {canManageChannels && (
          <>
            <ContextMenuItem icon={IconPlus} onSelect={onCreateChannel}>
              Create channel
            </ContextMenuItem>
            <ContextMenuItem icon={IconFolder} onSelect={onCreateCategory}>
              Create category
            </ContextMenuItem>
          </>
        )}
        {me.preferences.developerMode && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem icon={IconIdCard} onSelect={() => void copyId()}>
              Copy category ID
            </ContextMenuItem>
          </>
        )}
        {canManageChannels && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem icon={IconTrash} danger onSelect={() => setTimeout(() => void remove(), 0)}>
              Delete category
            </ContextMenuItem>
          </>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
}
